import { useEffect, useMemo, useRef, useState } from "react";
import config from "./config";
import { cachedPost } from "./apiCache";

/**
 * A creator's custom menu page — /membership/:token/page?id=…
 *
 * Shows the embed code the creator pasted in Settings → Membership Menus →
 * Custom menus (a booking calendar, a form, a video, a document…). Two ways:
 *
 * 1. Iframe-only code — what YouTube, Vimeo, Loom, Google, Calendly, Typeform…
 *    hand out as "embed code". It is rebuilt from a whitelist (http(s) src, a
 *    few layout attributes, plain wrapper tags) and placed straight on the
 *    page. Each frame belongs to its provider's own site, so it cannot reach
 *    this app's storage, and players keep the origin and referrer they need —
 *    YouTube, for one, will not play inside a sandbox.
 * 2. Anything else (HTML, scripts, widgets) runs in a SANDBOXED iframe:
 *    scripts, forms and popups are allowed, but not same-origin, so the snippet
 *    cannot read the member's session in localStorage. A tiny script inside
 *    reports the content's height, so the page grows to fit instead of
 *    scrolling inside a box.
 */

const MIN_HEIGHT = 80;

const readScheme = () => (document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light");

const IFRAME_ATTRS = new Set(["src", "width", "height", "title", "allow", "allowfullscreen", "frameborder", "loading", "referrerpolicy", "scrolling", "style", "name"]);
const WRAPPER_TAGS = new Set(["DIV", "P", "SPAN", "CENTER", "BR", "A"]);
const WRAPPER_ATTRS = new Set(["style", "class", "align"]);
// Players whose frame should fill the width at the video's own shape.
const VIDEO_HOST = /(^|\.)(youtube\.com|youtube-nocookie\.com|vimeo\.com|loom\.com|wistia\.(com|net)|vidyard\.com|dailymotion\.com)$/i;
// Scripts a provider adds beside its iframe that the frame doesn't need to show
// (Vimeo's player API), so the code still counts as iframe-only.
const OPTIONAL_SCRIPT = /^https:\/\/player\.vimeo\.com\/api\/player\.js/i;

const httpUrl = (raw) => {
  try {
    const u = new URL(raw || "", window.location.href);
    return /^https?:$/.test(u.protocol) ? u : null;
  } catch {
    return null;
  }
};

/**
 * The embed as clean HTML when it is only iframes in plain wrappers, else null
 * (the code needs the sandbox). Anything unexpected — another tag, a non-http
 * src, a frame pointing back at this app — sends it to the sandbox.
 */
function iframeOnlyHtml(code) {
  let body;
  try {
    body = new DOMParser().parseFromString(`<!doctype html><body>${code}`, "text/html").body;
  } catch {
    return null;
  }
  body.querySelectorAll("script").forEach((el) => {
    if (OPTIONAL_SCRIPT.test(el.getAttribute("src") || "")) el.remove();
  });
  if (!body.querySelector("iframe")) return null;

  for (const el of Array.from(body.querySelectorAll("*"))) {
    if (el.tagName === "IFRAME") {
      const url = httpUrl(el.getAttribute("src"));
      if (!url || url.origin === window.location.origin) return null;
      for (const a of Array.from(el.attributes)) if (!IFRAME_ATTRS.has(a.name.toLowerCase())) el.removeAttribute(a.name);
      el.setAttribute("src", url.href);
      if (!el.getAttribute("style")) {
        // Fixed-size snippets (width="560" height="315") would sit small in a
        // corner: fill the width, keeping a video's shape or the given height.
        const w = parseInt(el.getAttribute("width"), 10);
        const h = parseInt(el.getAttribute("height"), 10);
        const pct = /%/.test(el.getAttribute("height") || "");
        if (VIDEO_HOST.test(url.hostname) && w > 0 && h > 0 && !/%/.test(el.getAttribute("width") || "")) {
          el.setAttribute("style", `width:100%;height:auto;aspect-ratio:${w}/${h}`);
        } else {
          el.setAttribute("style", `width:100%;height:${h > 0 && !pct ? h : 640}px`);
        }
      }
      continue;
    }
    if (!WRAPPER_TAGS.has(el.tagName)) return null;
    for (const a of Array.from(el.attributes)) {
      const name = a.name.toLowerCase();
      if (el.tagName === "A" && name === "href") continue;
      if (!WRAPPER_ATTRS.has(name)) el.removeAttribute(a.name);
    }
    if (el.tagName === "A") {
      const url = httpUrl(el.getAttribute("href"));
      if (url) {
        el.setAttribute("href", url.href);
        el.setAttribute("target", "_blank");
        el.setAttribute("rel", "noopener noreferrer");
      } else {
        el.removeAttribute("href");
      }
    }
  }
  return body.innerHTML;
}

// The frame's color-scheme must match the page's: when they differ, Chrome
// paints the frame on an opaque white (or black) canvas instead of letting the
// card show through. Height is measured on <body> (a flow-root, so child
// margins count) — the root element never reports less than the frame's own
// height, which would stop the frame from ever shrinking.
function frameDoc(embed, { scheme, ink, link }) {
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><meta name="color-scheme" content="${scheme}"><base target="_blank">
<style>:root{color-scheme:${scheme}}html,body{margin:0;padding:0;background:transparent;color:${ink};font-family:system-ui,-apple-system,"Segoe UI",sans-serif}
body{display:flow-root}a{color:${link}}iframe,video,img,embed,object{max-width:100%}iframe{border:0}</style></head>
<body>${embed}
<script>(function(){function send(){try{var b=document.body;parent.postMessage({__customPage:1,h:Math.ceil(Math.max(b.scrollHeight,b.getBoundingClientRect().height))},"*")}catch(e){}}
if(window.ResizeObserver){new ResizeObserver(send).observe(document.body)}window.addEventListener("load",send);setTimeout(send,300);setTimeout(send,1500);send()})();</script>
</body></html>`;
}

export default function CustomPage({ token, member, pageId, primary = "#0274F7", showTitle = true, onBack }) {
  const [state, setState] = useState({ loading: true, page: null, error: "" });
  const [height, setHeight] = useState(420);
  const frameRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const memberToken = member?.member_token;
    if (!memberToken || !pageId) {
      setState({ loading: false, page: null, error: memberToken ? "This page is no longer available." : "Sign in to see this page." });
      return undefined;
    }
    setState({ loading: true, page: null, error: "" });
    cachedPost(
      `${config.COMMUNITY_API_URL}/membership-custom-page`,
      { community_token: token, member_token: memberToken, page_id: pageId },
      { ttl: 30000 }
    )
      .then((r) => {
        if (!alive) return;
        if (r.data?.error === 0 && r.data.page) setState({ loading: false, page: r.data.page, error: "" });
        else setState({ loading: false, page: null, error: r.data?.msg || "This page is no longer available." });
      })
      .catch(() => alive && setState({ loading: false, page: null, error: "This page didn't load. Try again in a moment." }));
    return () => {
      alive = false;
    };
  }, [token, member?.member_token, pageId]);

  // Height reports from inside the frame. Checked against the frame's own
  // window — any other frame on the page posting the same shape is ignored.
  useEffect(() => {
    const onMsg = (e) => {
      if (!frameRef.current || e.source !== frameRef.current.contentWindow) return;
      const h = Number(e.data?.h);
      if (e.data?.__customPage && h > 0) setHeight(Math.max(MIN_HEIGHT, Math.min(h, 20000)));
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  // The frame follows the member's light/dark mode (it can't read the page's
  // CSS), and redraws when the header toggle flips it.
  const [scheme, setScheme] = useState(readScheme);
  useEffect(() => {
    const root = document.documentElement;
    const obs = new MutationObserver(() => setScheme(readScheme()));
    obs.observe(root, { attributes: true, attributeFilter: ["data-theme", "data-tpl"] });
    return () => obs.disconnect();
  }, []);

  // Iframe-only code goes straight on the page (see the note at the top).
  const direct = useMemo(() => (state.page ? iframeOnlyHtml(state.page.embed) : null), [state.page]);

  const doc = useMemo(() => {
    if (!state.page || direct !== null) return "";
    let ink = scheme === "dark" ? "#f3f4f6" : "#111827";
    try {
      const v = getComputedStyle(document.documentElement).getPropertyValue("--c-gray-900").trim();
      if (v) ink = `rgb(${v})`;
    } catch {
      /* default ink */
    }
    return frameDoc(state.page.embed, { scheme, ink, link: primary });
  }, [state.page, direct, scheme, primary]);

  if (state.loading) {
    return (
      <div className="space-y-4" aria-busy="true">
        {showTitle ? <div className="h-7 w-48 animate-pulse rounded-lg bg-gray-100" /> : null}
        <div className="h-[420px] animate-pulse rounded-2xl border border-gray-200 bg-surface" />
      </div>
    );
  }

  if (!state.page) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-200 bg-surface px-6 py-14 text-center">
        <p className="text-[15px] font-semibold text-gray-800">{state.error}</p>
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="mt-4 rounded-xl px-4 py-2 text-[13.5px] font-bold transition hover:opacity-90"
            style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
          >
            Go back
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {showTitle ? <h2 className="text-[24px] font-bold tracking-tight text-gray-900">{state.page.label}</h2> : null}
      {direct !== null ? (
        // contain:paint keeps a creator's position:fixed wrapper inside the card.
        <div
          className="overflow-hidden rounded-2xl border border-gray-200 bg-surface shadow-sm [contain:paint] [&_iframe]:block [&_iframe]:max-w-full [&_iframe]:border-0"
          dangerouslySetInnerHTML={{ __html: direct }}
        />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-surface shadow-sm">
          <iframe
            ref={frameRef}
            title={state.page.label}
            srcDoc={doc}
            // No allow-same-origin, on purpose — see the note at the top.
            sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-presentation allow-downloads"
            allow="autoplay; fullscreen; encrypted-media; picture-in-picture; clipboard-write"
            className="block w-full"
            style={{ height }}
          />
        </div>
      )}
    </div>
  );
}
